
import { useTranslation } from 'react-i18next';
import { useRef, useEffect, useState } from 'react';

export default function Footer() {
  const { t } = useTranslation();
  const footerRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Intersection Observer for fade-in effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => observer.disconnect();
  }, []);
  
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer 
      ref={footerRef}
      className={`bg-[#F8FAFC] text-[#0F172A] border-t border-gray-200 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Mobile: Compact footer */}
        <div className="md:hidden py-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img 
              src="https://static.readdy.ai/image/90e3b7d98f6a66a16793c7597f1aaf38/28355f2063df5ee844bb8591b15f95b6.jpeg" 
              alt="BRAX Facility Group"
              className="h-10 w-auto object-contain rounded-lg"
            />
          </div>
          
          {/* Quick Links - Horizontal scroll */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <button onClick={() => scrollToSection('services')} className="text-xs text-gray-600 hover:text-[#14B8A6]">
              {t('footer.services')}
            </button>
            <span className="text-gray-300">•</span>
            <button onClick={() => scrollToSection('environments')} className="text-xs text-gray-600 hover:text-[#14B8A6]">
              {t('nav.environments')}
            </button>
            <span className="text-gray-300">•</span>
            <button onClick={() => scrollToSection('projects')} className="text-xs text-gray-600 hover:text-[#14B8A6]">
              {t('nav.serviceAreas')}
            </button>
            <span className="text-gray-300">•</span>
            <button onClick={() => scrollToSection('about')} className="text-xs text-gray-600 hover:text-[#14B8A6]">
              {t('footer.aboutUs')}
            </button>
            <span className="text-gray-300">•</span>
            <button onClick={() => scrollToSection('contact')} className="text-xs text-gray-600 hover:text-[#14B8A6]">
              {t('footer.contact')}
            </button>
          </div>

          {/* Email */}
          <a 
            href="mailto:contact@braxfacility.com" 
            className="flex items-center justify-center gap-2 text-[#14B8A6] text-sm font-medium mb-6"
          >
            <i className="ri-mail-line"></i>
            contact@braxfacility.com
          </a>

          {/* Copyright */}
          <div className="text-center text-xs text-gray-500 space-y-2">
            <p>© 2026 Brax Facility Services Group</p>
            <div className="flex items-center justify-center gap-3">
              <a href="#" className="hover:text-[#14B8A6]">{t('footer.privacy')}</a>
              <span>•</span>
              <a href="#" className="hover:text-[#14B8A6]">{t('footer.terms')}</a>
              <span>•</span>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="nofollow" className="hover:text-[#14B8A6]">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>

        {/* Desktop: Full footer */}
        <div className="hidden md:block py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10">
            {/* Logo & Description */}
            <div className={`lg:col-span-2 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://static.readdy.ai/image/90e3b7d98f6a66a16793c7597f1aaf38/28355f2063df5ee844bb8591b15f95b6.jpeg" 
                  alt="BRAX Facility Group"
                  className="h-12 w-auto object-contain rounded-lg"
                />
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t('footer.description')}
              </p>
              <a 
                href="mailto:contact@braxfacility.com" 
                className="inline-flex items-center gap-2 text-[#14B8A6] hover:text-[#0D9488] transition-colors font-medium"
              >
                <i className="ri-mail-line"></i>
                contact@braxfacility.com
              </a>
            </div>

            {/* Services */}
            <div className={`transition-all duration-700 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <h4 className="font-semibold text-lg mb-6">{t('footer.servicesTitle')}</h4>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-600 hover:text-[#14B8A6] transition-colors text-left text-sm">
                    {t('footer.service1')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-600 hover:text-[#14B8A6] transition-colors text-left text-sm">
                    {t('footer.service2')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-600 hover:text-[#14B8A6] transition-colors text-left text-sm">
                    {t('footer.service3')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-600 hover:text-[#14B8A6] transition-colors text-left text-sm">
                    {t('footer.service4')}
                  </button>
                </li>
              </ul>
            </div>

            {/* Company & Navigation */}
            <div className={`transition-all duration-700 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <h4 className="font-semibold text-lg mb-6">{t('footer.companyTitle')}</h4>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-gray-600 hover:text-[#14B8A6] transition-colors text-left text-sm">
                    {t('footer.aboutUs')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-600 hover:text-[#14B8A6] transition-colors text-left text-sm">
                    {t('footer.services')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('environments')} className="text-gray-600 hover:text-[#14B8A6] transition-colors text-left text-sm">
                    {t('nav.environments')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('projects')} className="text-gray-600 hover:text-[#14B8A6] transition-colors text-left text-sm">
                    {t('nav.serviceAreas')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-gray-600 hover:text-[#14B8A6] transition-colors text-left text-sm">
                    {t('footer.contact')}
                  </button>
                </li>
              </ul>
            </div>

            {/* Service Areas */}
            <div className={`transition-all duration-700 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <h4 className="font-semibold text-lg mb-6">{t('footer.areasTitle')}</h4>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => scrollToSection('projects')} className="text-gray-600 hover:text-[#14B8A6] transition-colors text-left text-sm">
                    Topanga
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('projects')} className="text-gray-600 hover:text-[#14B8A6] transition-colors text-left text-sm">
                    Pasadena
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('projects')} className="text-gray-600 hover:text-[#14B8A6] transition-colors text-left text-sm">
                    Santa Ana
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('projects')} className="text-gray-600 hover:text-[#14B8A6] transition-colors text-left text-sm">
                    Torrance
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Desktop Copyright */}
        <div className={`hidden md:flex py-6 border-t border-gray-200 flex-col md:flex-row justify-between items-center gap-4 transition-all duration-700 delay-600 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p className="text-gray-500 text-sm">
            © 2026 Brax Facility Services Group. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <a href="#" className="hover:text-[#14B8A6] transition-colors">
              {t('footer.privacy')}
            </a>
            <a href="#" className="hover:text-[#14B8A6] transition-colors">
              {t('footer.terms')}
            </a>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="nofollow" className="hover:text-[#14B8A6] transition-colors">
              Powered by Readdy
            </a>
            <button onClick={scrollToTop} className="w-10 h-10 rounded-lg bg-gradient-to-r from-[#14B8A6] to-[#0D9488] flex items-center justify-center hover:from-[#0D9488] hover:to-[#14B8A6] transition-colors cursor-pointer">
              <i className="ri-arrow-up-line text-white"></i>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
